import { CSSProperties, useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { useFormState } from 'informed';
import { actions } from 'store';
import { Alert } from 'components/common';

interface Props {
  detectRemovedItems?: boolean;
  initialValues: unknown;
  styles?: CSSProperties;
}

const UnsavedFormChanges = ({
  detectRemovedItems = false,
  initialValues,
  styles
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { hasUnsavedChanges, refreshInitialState }
  } = useReduxState();

  const formState = useFormState<{ [key: string]: unknown }>();

  const [initialState, setInitialState] = useState(initialValues || null);

  useEffect(() => {
    if (refreshInitialState) {
      setInitialState(JSON.parse(JSON.stringify(formState.values)));
      dispatch(actions.updatePageState({ refreshInitialState: false }));
    }
  }, [refreshInitialState]);

  useEffect(() => {
    lookForChanges();
  }, [formState, initialState]);

  const lookForChanges = () => {
    let contentChanged = false;

    const compareValues = (initialValue: unknown, currentValue: unknown) => {
      try {
        if (contentChanged) return;

        const currentValueEmpty =
          currentValue === undefined ||
          currentValue === null ||
          (Array.isArray(currentValue) && !currentValue.length) ||
          (typeof currentValue === 'object' &&
            !Object.keys(currentValue).length);

        const initialValueEmpty =
          initialValue === undefined ||
          initialValue === null ||
          (Array.isArray(initialValue) && !initialValue.length) ||
          (typeof initialValue === 'object' &&
            !Object.keys(initialValue).length);

        if (initialValueEmpty && currentValueEmpty) return;
        else if (initialValueEmpty && !currentValueEmpty) {
          // there is a registered form value that was not initialized
          // it could mean that a defualt value has to be set, so indicate changes
          contentChanged = true;
          return;
        } else if (!initialValueEmpty && currentValueEmpty) {
          if (detectRemovedItems) {
            contentChanged = true;
          }
          // there is an initial value that does not exist in the current form
          // this might be just that the form element for that was not initialized
          // as it depends on another field value, so we ignore it for now
          return;
        }

        if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
          if (currentValue.length !== initialValue.length) {
            contentChanged = true;
            return;
          }

          for (let i = 0; i < currentValue.length; i++) {
            compareValues(initialValue[i], currentValue[i]);
          }
        } else if (
          typeof currentValue === 'object' &&
          typeof initialValue === 'object'
        ) {
          if (
            detectRemovedItems &&
            Object.keys(currentValue).length < Object.keys(initialValue).length
          ) {
            contentChanged = true;
            return;
          }

          const keys = Array.from(
            new Set([
              ...Object.keys(currentValue),
              ...Object.keys(initialValue)
            ])
          );

          for (const key of keys) {
            const initial = initialValue[key];
            const current = currentValue[key];
            compareValues(initial, current);
          }
        } else {
          contentChanged =
            JSON.stringify(initialValue) !== JSON.stringify(currentValue);
          return;
        }
      } catch (err) {
        contentChanged = true;
        return;
      }
    };

    compareValues(initialState, formState.values);

    if (!hasUnsavedChanges && contentChanged)
      dispatch(actions.updatePageState({ hasUnsavedChanges: true }));
    else if (hasUnsavedChanges && !contentChanged)
      dispatch(actions.updatePageState({ hasUnsavedChanges: false }));
  };

  return hasUnsavedChanges && Boolean(initialState) ? (
    <Alert
      style={{ marginBottom: 5, ...styles }}
      text={messages.commonUnsaved}
      type="warning-light"
    />
  ) : null;
};

export default UnsavedFormChanges;
